import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3, ShieldCheck, FileText, AlertTriangle, Bug, Accessibility,
  Zap, Shield, Globe, History, UserCog, Mail, Users, Package, Lock, Inbox,
  Menu, X, LogOut,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import DbStatusBadge from './DbStatusBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Dashboard chrome (sidebar + main pane) used by every authenticated
 * dashboard route. Nav items are filtered by RBAC `canRead()` and the
 * `Findings` row is currently locked for demo purposes.
 */
function DashboardLayout({ children }) {
  const { user, logout, canRead } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [pendingAccessRequests, setPendingAccessRequests] = useState(0);

  // Poll pending access-request count so owners see a red dot the
  // moment new prospects submit the public form. 60s cadence — cheap
  // count() query on Mongo.
  useEffect(() => {
    // Only owners/admins with clients-read can see the endpoint.
    if (!canRead('clients')) return undefined;

    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await fetch(`${API_URL}/api/access-requests/pending-count`, {
          credentials: 'include',
        });
        if (!r.ok || cancelled) return;
        const { count } = await r.json();
        if (!cancelled) setPendingAccessRequests(count || 0);
      } catch (_) {
        /* silent — the badge is nice-to-have, don't spam toasts */
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [canRead]);

  const navItems = [
    { path: '/dashboard',            icon: BarChart3,     label: 'Dashboard' },
    { path: '/controls',             icon: ShieldCheck,   label: 'Controls',         resource: 'controls' },
    { path: '/policies',             icon: FileText,      label: 'Policies',         resource: 'policies' },
    { path: '/findings',             icon: AlertTriangle, label: 'Findings',         resource: 'findings',
      locked: true, lockedReason: 'Restricted — access not granted in this environment' },
    { path: '/pentest',              icon: Bug,           label: 'Security Scans',   resource: 'pentest' },
    { path: '/accessibility',        icon: Accessibility, label: 'Accessibility',    resource: 'accessibility' },
    { path: '/monitors',             icon: Zap,           label: 'Monitors',         resource: 'monitors' },
    { path: '/evidence',             icon: Shield,        label: 'Evidence',         resource: 'evidence' },
    { path: '/caiq',                 icon: Globe,         label: 'CAIQ Responses',   resource: 'caiq' },
    { path: '/reports',              icon: FileText,      label: 'Reports',          resource: 'reports' },
    { path: '/roster',               icon: UserCog,       label: 'Roster',           resource: 'roster' },
    { path: '/audit-trail',          icon: History,       label: 'Audit Trail',      resource: 'audit_trail' },
    { path: '/trust-center-preview', icon: Globe,         label: 'Trust Center',     resource: 'trust_center' },
    { path: '/trust-requests',       icon: Mail,          label: 'Trust Requests',   resource: 'trust_center' },
    { path: '/clients',              icon: Users,         label: 'Client Mgmt',      resource: 'clients' },
    { path: '/access-control',       icon: Lock,          label: 'Access Control',   resource: 'clients' },
    { path: '/access-requests',      icon: Inbox,         label: 'Access Requests',  resource: 'clients' },
    { path: '/audit',                icon: Package,       label: 'Audit Export',     resource: 'audit_export' },
    { path: '/users',                icon: Lock,          label: 'User Management',  resource: 'user_management' },
  ];

  return (
    <div className={`dashboard-layout ${darkMode ? 'dark' : 'light'}`}>
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="mobile-menu-toggle"
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/eyvo-logo.png" alt="Eyvo" className="sidebar-logo-img" />
            <span>Procurement</span>
          </div>
          <div className="sidebar-subtitle">SOC 2 Compliance Platform</div>
        </div>

        <nav className="sidebar-nav">
          {navItems
            .filter((i) => !i.resource || canRead(i.resource))
            .map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              if (item.locked) {
                return (
                  <div
                    key={item.path}
                    className="nav-item nav-item-locked"
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}-locked`}
                    title={item.lockedReason || 'Restricted'}
                    aria-disabled="true"
                    style={{
                      cursor: 'not-allowed',
                      opacity: 0.55,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      position: 'relative',
                    }}
                  >
                    <Icon size={18} />
                    <span
                      style={{
                        flex: 1,
                        textDecoration: 'line-through',
                        textDecorationColor: 'rgba(220,38,38,0.55)',
                      }}
                    >
                      {item.label}
                    </span>
                    <Lock size={14} style={{ color: '#dc2626', flexShrink: 0 }} />
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                >
                  <Icon size={18} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.path === '/access-requests' && pendingAccessRequests > 0 && (
                    <span
                      data-testid="nav-access-requests-badge"
                      title={`${pendingAccessRequests} pending access request${pendingAccessRequests === 1 ? '' : 's'}`}
                      style={{
                        background: '#dc2626',
                        color: '#fff',
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '2px 7px',
                        minWidth: 18,
                        textAlign: 'center',
                        lineHeight: 1.4,
                        boxShadow: '0 0 0 2px rgba(220,38,38,0.25)',
                      }}
                    >
                      {pendingAccessRequests > 9 ? '9+' : pendingAccessRequests}
                    </span>
                  )}
                </Link>
              );
            })}
        </nav>

        <div className="sidebar-footer">
          <DbStatusBadge />
          <button onClick={() => setDarkMode(!darkMode)} className="theme-toggle">
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </button>
          <div className="user-info">
            <img
              src={user?.picture || 'https://via.placeholder.com/40'}
              alt={user?.name}
              className="user-avatar"
            />
            <div className="user-details">
              <p className="user-name">{user?.name}</p>
              <p className="user-email">{user?.email}</p>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        {children}
      </main>
    </div>
  );
}

export default DashboardLayout;
